import React, { useState, useEffect } from 'react';
import '../scss/FeaturesComparison.scss';
import { StaticHeader, StaticFooter } from '../components';

const FeaturesComparison = () => {
    const [activeTab, setActiveTab] = useState('ltd');

    const [billingPeriod, setBillingPeriod] = useState('monthly');

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const subscriptionPlans = [
        {
            name: 'Free',
            price: '$0',
            period: 'Forever',
            color: '#6b7280',
            popular: false
        },
        {
            name: 'Standard',
            price: billingPeriod === 'monthly' ? '$4.99' : '$49.99',
            period: billingPeriod === 'monthly' ? 'per seat/month' : 'per seat/year',
            color: '#10b981',
            popular: false
        },
        {
            name: 'Premium',
            price: billingPeriod === 'monthly' ? '$9.99' : '$95.99',
            period: billingPeriod === 'monthly' ? 'per seat/month' : 'per seat/year',
            color: '#3b82f6',
            popular: true
        },
        {
            name: 'Enterprise',
            price: 'Contact',
            period: 'Sales',
            color: '#667eea',
            popular: false
        }
    ];

    const ltdPlans = [
        {
            name: 'Tier 1',
            price: '$99',
            period: 'Lifetime',
            color: '#f59e0b',
            popular: false
        },
        {
            name: 'Tier 2',
            price: '$199',
            period: 'Lifetime',
            color: '#3b82f6',
            popular: false
        },
        {
            name: 'Tier 3',
            price: '$299',
            period: 'Lifetime',
            color: '#667eea',
            popular: true
        }
    ];

    const plans = activeTab === 'ltd' ? ltdPlans : subscriptionPlans;

    // Recurring subscription features (from Billing.js)
    const subscriptionFeatures = [
        {
            name: 'Free Features (Everyone)',
            icon: '🆓',
            features: [
                {
                    name: 'Kanban Board View',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Drag & Drop Cards',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Comments & Replies',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Labels & Tags',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Due Dates',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Slack Notifications',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Push Notifications',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Basic Checklists',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Mentions & Real-time Notifications',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Custom Backgrounds & Stickers',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Last Activity Status for All Members',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Board Search & Filters',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Member Assignment',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Card Covers & Colors',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'List Management',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Board Invitations & Join Links',
                    free: true,
                    standard: true,
                    premium: true,
                    enterprise: true
                }

            ]
        },
        {
            name: 'Core',
            icon: '🚀',
            features: [
                {
                    name: 'Cards',
                    free: 'Unlimited',
                    standard: 'Unlimited',
                    premium: 'Unlimited',
                    enterprise: 'Unlimited'
                },
                {
                    name: 'Boards',
                    free: 'Up to 15',
                    standard: 'Unlimited',
                    premium: 'Unlimited',
                    enterprise: 'Unlimited'
                },
                {
                    name: 'Collaborators',
                    free: '15',
                    standard: 'Unlimited',
                    premium: 'Unlimited',
                    enterprise: 'Unlimited'
                },
                {
                    name: 'File Attachments',
                    free: '20 MB per file',
                    standard: '250 MB per file',
                    premium: '250 MB per file',
                    enterprise: '250 MB per file'
                },
                {
                    name: 'Storage',
                    free: '512 MB per workspace',
                    standard: 'Unlimited',
                    premium: 'Unlimited',
                    enterprise: 'Unlimited'
                },

            ]
        },
        {
            name: 'Advanced Features',
            icon: '📋',
            features: [
                {
                    name: 'Saved Searches',
                    free: false,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Advanced Checklists',
                    free: false,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Board Backgrounds & Stickers',
                    free: false,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Online Status Visibility',
                    free: false,
                    standard: true,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Pro Verified Badge',
                    free: false,
                    standard: true,
                    premium: true,
                    enterprise: true
                }
            ]
        },
        {
            name: 'Premium Features',
            icon: '⭐',
            features: [
                {
                    name: 'Custom Stickers & Emojis',
                    free: false,
                    standard: false,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Data Export (JSON/CSV)',
                    free: false,
                    standard: false,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Extended Activity Logs',
                    free: false,
                    standard: false,
                    premium: true,
                    enterprise: true
                },
                {
                    name: 'Priority Support',
                    free: false,
                    standard: false,
                    premium: true,
                    enterprise: true
                }
            ]
        },
        {
            name: 'Enterprise Features',
            icon: '🏢',
            features: [
                {
                    name: 'Dedicated Support Contact',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                },
                {
                    name: 'Attachment Restrictions',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                },
                {
                    name: 'User Role Management',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                },
                {
                    name: 'Organization-wide Permissions',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                },
                {
                    name: 'Public Board Visibility Controls',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                },
                {
                    name: 'SSO (Coming Soon)',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                },
                {
                    name: 'Custom Security Policies',
                    free: false,
                    standard: false,
                    premium: false,
                    enterprise: true
                }
            ]
        }
    ];

    // AppSumo LTD features (from AppSumoTab.js)
    const ltdFeatures = [
        {
            name: 'Free Features (Everyone)',
            icon: '🆓',
            features: [
                {
                    name: 'Kanban Board View',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Drag & Drop Cards',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Comments & Replies',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Labels & Tags',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Due Dates',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Slack Notifications',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Push Notifications',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Basic Checklists',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Mentions & Real-time Notifications',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Custom Backgrounds & Stickers',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Last Activity Status for All Members',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Board Search & Filters',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Member Assignment',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Card Covers & Colors',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'List Management',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Board Invitations & Join Links',
                    tier1: true,
                    tier2: true,
                    tier3: true
                }
            ]

        },
        {
            name: 'Core',
            icon: '🚀',
            features: [
                {
                    name: 'Lifetime Access',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Pro Workspace',
                    tier1: '1 Pro Workspace',
                    tier2: '1 Pro Workspace',
                    tier3: '1 Pro Workspace'
                },
                {
                    name: 'Team Seats',
                    tier1: '10 team seats',
                    tier2: '20 team seats',
                    tier3: '30 team seats'
                },
                {
                    name: 'Storage',
                    tier1: '5GB storage',
                    tier2: '10GB storage',
                    tier3: '20GB storage'
                }
            ]
        },
        {
            name: 'Boards',
            icon: '📋',
            features: [
                {
                    name: 'Boards',
                    tier1: 'Boards: 25',
                    tier2: 'Boards: 50',
                    tier3: 'Boards: Unlimited'
                },
                {
                    name: 'Unlimited Cards',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'File Attachments',
                    tier1: '250 MB per file',
                    tier2: '250 MB per file',
                    tier3: '250 MB per file'
                },
                {
                    name: 'Advanced Checklists',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Saved Searches',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Board Backgrounds',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Public Board Visibility Controls',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Task Timers (Stopwatch & Countdown)',
                    tier1: true,
                    tier2: true,
                    tier3: true
                }
            ]
        },
        {
            name: 'Analytics',
            icon: '📊',
            features: [
                {
                    name: 'Basic Board Analytics',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Advanced Board Analytics',
                    tier1: false,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'User Productivity Metrics',
                    tier1: false,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Performance Insights',
                    tier1: false,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Activity Tracking',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Extended Activity Logs',
                    tier1: false,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Analytics Data Export',
                    tier1: false,
                    tier2: false,
                    tier3: true
                }
            ]
        },
        {
            name: 'Collaboration',
            icon: '👥',
            features: [
                {
                    name: 'User Role Management',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Team Member Management',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Last Activity Status for All Members',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Online Status Visibility',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Pro Verified Badge',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Priority Support',
                    tier1: false,
                    tier2: true,
                    tier3: true
                }
            ]
        },
        {
            name: 'Integrations',
            icon: '🔗',
            features: [
                {
                    name: 'Trello Import',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'FluentBoards Import',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Unsplash Integration',
                    tier1: true,
                    tier2: true,
                    tier3: true
                },
                {
                    name: 'Advanced Search Capabilities',
                    tier1: false,
                    tier2: true,
                    tier3: true
                }
            ]
        },
        {
            name: 'Exclusive',
            icon: '⭐',
            features: [
                {
                    name: 'Lifetime White-label Branding (Tier 3 Exclusive)',
                    tier1: false,
                    tier2: false,
                    tier3: true
                },
                {
                    name: 'Custom Domain Support',
                    tier1: false,
                    tier2: false,
                    tier3: true
                },
                {
                    name: 'Complete Brand Customization',
                    tier1: false,
                    tier2: false,
                    tier3: true
                },
                {
                    name: 'Remove Zoobbe Branding',
                    tier1: false,
                    tier2: false,
                    tier3: true
                }
            ]
        }
    ];

    // Use different feature sets based on active tab
    const featureCategories = activeTab === 'ltd' ? ltdFeatures : subscriptionFeatures;

    const renderFeatureValue = (value, planColor) => {
        if (typeof value === 'boolean') {
            return value ? (
                <span className="feature-check" style={{ color: planColor }}>✓</span>
            ) : (
                <span className="feature-cross">✗</span>
            );
        }
        return <span className="feature-text">{value}</span>;
    };

    return (
        <>
            <StaticHeader />
            <div className="features-comparison">
                <div className="comparison-header">
                    <h1>Complete Feature Comparison</h1>
                    <p>Compare all Zoobbe plans and find the perfect fit for your team</p>
                </div>

            {/* Subscription plans disabled - only showing AppSumo LTD */}
            {/* <div className="plan-tabs">
                <button
                    className={`tab-button ${activeTab === 'subscription' ? 'active' : ''}`}
                    onClick={() => setActiveTab('subscription')}
                >
                    Subscription Plans
                </button>
                <button
                    className={`tab-button ${activeTab === 'ltd' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ltd')}
                >
                    AppSumo LTD
                </button>
            </div> */}

            {/* Billing Period Toggle for Subscription Plans */}
            {activeTab === 'subscription' && (
                <div className="billing-toggle">
                    <button
                        className={`billing-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
                        onClick={() => setBillingPeriod('monthly')}
                    >
                        Monthly
                    </button>
                    <button
                        className={`billing-btn ${billingPeriod === 'annually' ? 'active' : ''}`}
                        onClick={() => setBillingPeriod('annually')}
                    >
                        Annually <span className="discount">SAVE 17%</span>
                    </button>
                </div>
            )}

            <div className={`plans-header ${activeTab === 'ltd' ? 'ltd-plans' : 'subscription-plans'}`}>
                <div className="feature-column-header"></div>
                {plans.map((plan, index) => (
                    <div key={index} className={`plan-header ${plan.popular ? 'popular' : ''}`}>
                        {plan.popular && <div className="popular-badge">Most Popular</div>}
                        <h3 style={{ color: plan.color }}>{plan.name}</h3>
                        <div className="plan-price">
                            <span className="price">{plan.price}</span>
                            <span className="period">{plan.period}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="features-table">
                {featureCategories.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="feature-category">
                        <div className={`category-header ${activeTab === 'ltd' ? 'ltd-plans' : 'subscription-plans'}`}>
                            <h4>
                                <span className="category-icon">{category.icon}</span>
                                {category.name}
                            </h4>
                            {activeTab === 'ltd' ? (
                                <>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </>
                            ) : (
                                <>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </>
                            )}
                        </div>

                        {category.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className={`feature-row ${activeTab === 'ltd' ? 'ltd-plans' : 'subscription-plans'}`}>
                                <div className="feature-name">{feature.name}</div>
                                <div className="feature-values">
                                    {plans.map((plan, planIndex) => {
                                        let planKey;
                                        if (activeTab === 'ltd') {
                                            planKey = plan.name.toLowerCase().includes('tier 1') ? 'tier1' :
                                                plan.name.toLowerCase().includes('tier 2') ? 'tier2' :
                                                    plan.name.toLowerCase().includes('tier 3') ? 'tier3' : null;
                                        } else {
                                            planKey = plan.name.toLowerCase().includes('enterprise') ? 'enterprise' :
                                                plan.name.toLowerCase().includes('premium') ? 'premium' :
                                                    plan.name.toLowerCase().includes('standard') ? 'standard' : 'free';
                                        }
                                        return (
                                            <div key={planIndex} className="feature-value" data-plan={plan.name}>
                                                {renderFeatureValue(feature[planKey], plan.color)}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            <div className="comparison-footer">
                <div className="cta-section">
                    <h3>Ready to get started?</h3>
                    <p>Choose the plan that best fits your team's needs</p>
                    <div className="cta-buttons">
                        {activeTab === 'ltd' ? (
                            <>
                                <button className="btn-primary" onClick={() => window.open('https://appsumo.com/zoobbe', '_blank')}>
                                    Grab The Deal
                                </button>
                                {/* Subscription plans disabled */}
                                {/* <button className="btn-secondary" onClick={() => setActiveTab('subscription')}>
                                    View Monthly Plans
                                </button> */}
                            </>
                        ) : (
                            <>
                                <button className="btn-primary" onClick={() => window.open('/signup', '_blank')}>
                                    Start Free Trial
                                </button>
                                <button className="btn-secondary" onClick={() => setActiveTab('ltd')}>
                                    View Lifetime Deals
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <StaticFooter />
        </div>
        </>
    );
};

export default FeaturesComparison;
